import { useState, useEffect } from "react";
import { fetchWithTimeout } from "../../App";
import "../../App.css";

const API_URL = process.env.REACT_APP_API_URL;

function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [menu, setMenu] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [openTime, setOpenTime] = useState("");
  const [closeTime, setCloseTime] = useState("");
  const [items, setItems] = useState([]);

  const fetchSessions = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/sessions`, { credentials: "include" });
      const data = await res.json();
      if (Array.isArray(data)) setSessions(data);
    } catch (err) {
      console.error("Error fetching sessions:", err);
    }
  };

  const fetchMenu = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/menu`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setMenu(data);
        setItems(data.map(m => ({ item_name: m.name, max_qty: "" })));
      }
    } catch (err) {
      console.error("Error fetching menu:", err);
    }
  };

  useEffect(() => {
    Promise.allSettled([fetchMenu(), fetchSessions()]);
  }, []);

  const updateItemQty = (index, value) => {
    setItems(prev => prev.map((item, i) =>
      i === index ? { ...item, max_qty: value } : item
    ));
  };

  const createSession = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!openTime || !closeTime) return setError("Please fill in both times.");
    if (new Date(openTime) >= new Date(closeTime)) return setError("Close time must be after open time.");

    const activeItems = items.filter(i => i.max_qty !== "" && Number(i.max_qty) > 0);
    if (activeItems.length === 0) return setError("Set a quantity limit for at least one item.");

    const toLocalTZ = (dt) => {
      const tzOffset = process.env.REACT_APP_TZ_OFFSET || "+03:00";
      return dt + ":00" + tzOffset;
    };

    try {
      const res = await fetch(`${API_URL}/api/admin/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          open_time: toLocalTZ(openTime),
          close_time: toLocalTZ(closeTime),
          items: activeItems.map(i => ({ ...i, max_qty: Number(i.max_qty) })),
        }),
      });

      const data = await res.json();
      if (!res.ok) return setError(data.error || "Failed to create session.");

      setSuccess(`Session created! Total capacity: ${activeItems.reduce((s, i) => s + Number(i.max_qty), 0)} orders`);
      setOpenTime("");
      setCloseTime("");
      setItems(menu.map(m => ({ item_name: m.name, max_qty: "" })));
      fetchSessions();
    } catch (err) {
      setError("Something went wrong.");
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this session?")) return;
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/admin/sessions/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || "Failed to delete session.");
      fetchSessions();
    } catch (err) {
      setError("Something went wrong.");
    }
  };

  const getSessionStatus = (session) => {
    const now = new Date();
    const open = new Date(session.open_time);
    const close = new Date(session.close_time);

    if (now < open) return { label: "Upcoming", color: "var(--orange)", bg: "#FEF4E4" };
    if (now > close) return { label: "Ended", color: "var(--text-muted)", bg: "var(--beige)" };

    if (session.item_limits?.length > 0) {
      const allFull = session.item_limits.every(
        i => parseInt(i.ordered_qty) >= parseInt(i.max_qty)
      );
      if (allFull) return { label: "Full", color: "var(--error)", bg: "#FDECEC" };
    }

    return { label: "Active", color: "var(--success)", bg: "#EDF5EE" };
  };

  const fmt = (dt) => new Date(dt).toLocaleString("en-MY", {
    dateStyle: "medium", timeStyle: "short"
  });

  const totalOrdered = (session) => parseInt(session.order_count || 0);
  const totalMax = (session) => session.item_limits?.reduce((s, i) => s + parseInt(i.max_qty), 0) || session.max_orders;

  return (
    <div style={{ maxWidth: "750px" }}>
      <h1 className="admin-page-heading">📅 Order Sessions</h1>
      <p className="admin-page-sub">Create and manage ordering windows</p>

      <div style={formCardStyle}>
        <h2 style={{ marginTop: 0, fontSize: "1.1rem", fontFamily: "'Playfair Display', Georgia, serif", color: "var(--text-main)" }}>Create New Session</h2>

        <form onSubmit={createSession}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div>
              <label style={labelStyle}>Opens at</label>
              <input
                type="datetime-local"
                value={openTime}
                onChange={(e) => setOpenTime(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Closes at</label>
              <input
                type="datetime-local"
                value={closeTime}
                onChange={(e) => setCloseTime(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <label style={{ ...labelStyle, marginBottom: "0.75rem" }}>
            Item Limits
            <span style={{ fontWeight: "normal", color: "var(--text-muted)", marginLeft: "0.5rem", textTransform: "none", letterSpacing: "normal" }}>
              — set how many of each item you're selling
            </span>
          </label>

          {items.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: "0.88rem" }}>Loading menu...</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
              {items.map((item, index) => (
                <div key={item.item_name} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  background: "var(--beige)",
                  border: "1px solid var(--border)",
                  borderRadius: "10px",
                  padding: "0.6rem 0.9rem",
                }}>
                  <span style={{ flex: 1, fontSize: "0.95rem", color: "var(--text-main)" }}>
                    {item.item_name}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <label style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>Max qty:</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={item.max_qty}
                      onChange={(e) => updateItemQty(index, e.target.value)}
                      style={{
                        width: "70px",
                        padding: "0.35rem 0.5rem",
                        borderRadius: "8px",
                        border: "1.5px solid var(--border)",
                        fontSize: "0.95rem",
                        textAlign: "center",
                        background: "var(--warm-white)",
                        color: "var(--text-main)",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {items.some(i => Number(i.max_qty) > 0) && (
            <p style={{ fontSize: "0.88rem", color: "var(--text-muted)", marginBottom: "1rem" }}>
              Total capacity:{" "}
              <strong style={{ color: "var(--text-main)" }}>
                {items.reduce((s, i) => s + (Number(i.max_qty) || 0), 0)} orders
              </strong>
            </p>
          )}

          {error && <p style={{ color: "var(--error)", fontSize: "0.9rem", margin: "0.5rem 0" }}>{error}</p>}
          {success && <p style={{ color: "var(--success)", fontSize: "0.9rem", margin: "0.5rem 0" }}>{success}</p>}

          <button type="submit" style={createButtonStyle}>
            + Create Session
          </button>
        </form>
      </div>

      <h2 style={{ fontSize: "1.1rem", marginTop: "2rem", fontFamily: "'Playfair Display', Georgia, serif", color: "var(--text-main)" }}>All Sessions</h2>

      {sessions.length === 0 ? (
        <p style={{ color: "var(--text-muted)" }}>No sessions yet.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {sessions.map((session) => {
            const status = getSessionStatus(session);
            const ordered = totalOrdered(session);
            const max = totalMax(session);
            const pct = Math.min((ordered / max) * 100, 100);

            return (
              <div key={session.id} style={sessionCardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>

                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                      <span style={{
                        background: status.bg, color: status.color,
                        padding: "0.2rem 0.7rem", borderRadius: "999px",
                        fontSize: "0.8rem", fontWeight: "bold",
                      }}>
                        {status.label}
                      </span>
                      <span style={{ fontSize: "0.85rem", color: "var(--text-main)", fontWeight: "bold" }}>
                        {ordered} / {max} total orders
                      </span>
                    </div>

                    <p style={{ margin: "0 0 0.25rem", fontSize: "0.88rem", color: "var(--text-muted)" }}>
                      🟢 Opens: {fmt(session.open_time)}
                    </p>
                    <p style={{ margin: "0 0 0.75rem", fontSize: "0.88rem", color: "var(--text-muted)" }}>
                      🔴 Closes: {fmt(session.close_time)}
                    </p>

                    {session.item_limits?.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "0.75rem" }}>
                        {session.item_limits.map(item => {
                          const itemOrdered = parseInt(item.ordered_qty);
                          const itemMax = parseInt(item.max_qty);
                          const itemPct = Math.min((itemOrdered / itemMax) * 100, 100);
                          const soldOut = itemOrdered >= itemMax;
                          return (
                            <div key={item.item_name}>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", marginBottom: "0.2rem" }}>
                                <span style={{ color: soldOut ? "var(--error)" : "var(--text-main)" }}>
                                  {soldOut ? "🔴" : "🟡"} {item.item_name}
                                </span>
                                <span style={{ color: "var(--text-muted)" }}>
                                  {itemOrdered} / {itemMax}
                                  {soldOut && <span style={{ color: "var(--error)", fontWeight: "bold", marginLeft: "0.4rem" }}>SOLD OUT</span>}
                                </span>
                              </div>
                              <div style={{ background: "var(--border)", borderRadius: "999px", height: "4px" }}>
                                <div style={{
                                  width: `${itemPct}%`,
                                  background: itemPct >= 100 ? "var(--error)" : itemPct >= 80 ? "var(--gold)" : "var(--success)",
                                  borderRadius: "999px",
                                  height: "4px",
                                }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={{ background: "var(--border)", borderRadius: "999px", height: "6px" }}>
                      <div style={{
                        width: `${pct}%`,
                        background: pct >= 100 ? "var(--error)" : pct >= 80 ? "var(--gold)" : "var(--success)",
                        borderRadius: "999px",
                        height: "6px",
                      }} />
                    </div>
                  </div>

                  {ordered === 0 && (
                    <button onClick={() => deleteSession(session.id)} style={deleteButtonStyle}>
                      🗑
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const formCardStyle = {
  background: "var(--warm-white)",
  border: "1px solid var(--border)",
  borderRadius: "16px",
  padding: "1.5rem",
  boxShadow: "0 4px 24px rgba(92, 51, 23, 0.08)",
};

const sessionCardStyle = {
  background: "var(--warm-white)",
  border: "1px solid var(--border)",
  borderRadius: "14px",
  padding: "1.25rem",
  boxShadow: "0 2px 12px rgba(92, 51, 23, 0.06)",
};

const labelStyle = {
  display: "block",
  fontWeight: "600",
  fontSize: "0.85rem",
  color: "var(--brown-mid)",
  marginBottom: "0.3rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const inputStyle = {
  display: "block",
  width: "100%",
  padding: "0.6rem 0.85rem",
  marginBottom: "1rem",
  borderRadius: "10px",
  border: "1.5px solid var(--border)",
  background: "var(--beige)",
  fontSize: "0.95rem",
  boxSizing: "border-box",
  color: "var(--text-main)",
};

const createButtonStyle = {
  background: "var(--orange)",
  color: "white",
  border: "none",
  padding: "0.65rem 1.5rem",
  borderRadius: "10px",
  fontSize: "1rem",
  fontWeight: "700",
  cursor: "pointer",
  boxShadow: "0 4px 14px rgba(224, 123, 57, 0.35)",
};

const deleteButtonStyle = {
  background: "#FDECEC",
  border: "1px solid #E8A8A8",
  borderRadius: "8px",
  padding: "0.4rem 0.7rem",
  cursor: "pointer",
  fontSize: "1rem",
  marginLeft: "1rem",
  flexShrink: 0,
};

export default Sessions;
